/**
 * Referral code handoff between the landing page and the onboarding flow.
 *
 * Landing and reader-web live on different subdomains (polyreader.net vs
 * app.polyreader.net), so localStorage doesn't carry between them. The
 * landing page forwards the ?ref= query param on every /auth link, and
 * this helper captures it into localStorage before the Cognito/Google
 * OAuth redirect loses the query string. OnboardingFlow then pulls it
 * out at mount time and pre-fills the affiliate code step.
 *
 * The code is cleared after the onboarding-complete POST succeeds so a
 * returning user doesn't see a stale banner on a re-signup.
 */

const STORAGE_KEY = 'polyreader_affiliate_code';

/**
 * Read `?ref=<code>` from the current URL and persist to localStorage
 * if present. Called from Auth.tsx and AuthEmail.tsx on mount — must
 * run before any OAuth / signup redirect that would lose the query
 * string.
 *
 * Idempotent: calling it twice with the same URL is a no-op.
 */
export function captureReferralCodeFromUrl(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref && ref.trim()) {
      localStorage.setItem(STORAGE_KEY, ref.trim());
    }
  } catch {
    // localStorage unavailable (e.g. private mode quotas) — silently skip.
  }
}

/**
 * Read the stored referral code without clearing it. Used as the lazy
 * initializer for the OnboardingFlow affiliate code state.
 */
export function readStoredReferralCode(): string {
  try {
    return localStorage.getItem(STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}

/**
 * Clear the stored referral code. Called after the onboarding-complete
 * POST succeeds so a returning user doesn't see a stale banner.
 */
export function clearStoredReferralCode(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // no-op
  }
}
